import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const menu = [
  { label: "CRAFTSMANSHIP", href: "#craftsmanship" },
  { label: "TECHNOLOGY", href: "#technology" },
  { label: "PRODUCT", href: "#product" },
  { label: "CATCH", href: "#products" },
  { label: "HOW TO", href: "/how-to#how-to-use" },
  { label: "NEWS", href: "#news" },
  { label: "CONTACT", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleMenuClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault();
    setOpen(false);

    // Full page navigation like /how-to
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }

    // Hash link on home page — smooth scroll
    if (isHome) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    // Hash link on other page — navigate to home with hash
    navigate(`/${href}`);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/85 backdrop-blur-md border-b border-background-200/60"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-6 md:px-10 h-20 flex items-center justify-between">
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            if (isHome) {
              window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
              navigate("/#top");
            }
          }}
          className="flex items-center gap-3 cursor-pointer group"
        >
          <div className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-flashlight-fill text-lg"></i>
          </div>
          <div className="leading-tight">
            <div
              className="text-foreground-950 tracking-widest text-lg"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              HAYABUSA WORKS
            </div>
            <div className="text-[10px] text-foreground-9500 tracking-[0.3em]">
              ハヤブサワークス
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {menu.map((m) => (
            <a
              key={m.label}
              href={m.href}
              onClick={(e) => handleMenuClick(e, m.href)}
              className="text-xs tracking-[0.25em] text-foreground-800 hover:text-primary-500 transition-colors cursor-pointer font-label"
            >
              {m.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="https://raigeki-hayabusa.stores.jp"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 pl-5 pr-2 py-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-xs tracking-[0.25em] whitespace-nowrap transition cursor-pointer font-label"
          >
            <span>公式オンラインショップ</span>
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-50 text-primary-500">
              <i className="ri-arrow-right-up-line text-sm"></i>
            </span>
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-900 hover:text-primary-500 cursor-pointer"
          aria-label="menu"
        >
          <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-background-50/95 backdrop-blur-md border-t border-background-200/60">
          <div className="px-6 py-6 flex flex-col gap-4">
            {menu.map((m) => (
              <a
                key={m.label}
                href={m.href}
                onClick={(e) => handleMenuClick(e, m.href)}
                className="text-sm tracking-[0.25em] text-foreground-800 hover:text-primary-500 cursor-pointer"
              >
                {m.label}
              </a>
            ))}
            <a
              href="https://raigeki-hayabusa.stores.jp"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 self-start px-5 py-3 rounded-full bg-primary-500 text-background-50 text-xs tracking-[0.25em] whitespace-nowrap cursor-pointer"
            >
              公式オンラインショップ →
            </a>
          </div>
        </div>
      )}
    </header>
  );
}